import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import type { Currency, CurrencyOption } from '@/types/creator';

/**
 * Accessible currency combobox, ported from the creator-portal prototype
 * (ROU-1192). Full keyboard support (arrows/Home/End/Enter/Escape) and
 * above/below flip based on available space.
 */

const OPTION_ROW_HEIGHT_PX = 46;
const MENU_PADDING_PX = 12;

type Props = {
    value: Currency;
    options: CurrencyOption[];
    onChange: (code: Currency) => void;
    labelId: string;
    disabled?: boolean;
};

const CurrencySelect = ({ value, options, onChange, labelId, disabled = false }: Props) => {
    const [open, setOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [placement, setPlacement] = useState<'above' | 'below'>('below');
    const rootRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listboxId = useId();
    const valueId = useId();
    const selectedIndex = Math.max(
        options.findIndex((option) => option.code === value),
        0
    );
    const selectedOption = options[selectedIndex];

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const closeOnOutsidePointer = (event: PointerEvent): void => {
            if (!rootRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener('pointerdown', closeOnOutsidePointer);
        return () => document.removeEventListener('pointerdown', closeOnOutsidePointer);
    }, [open]);

    const openList = (index: number = selectedIndex): void => {
        const triggerBounds = triggerRef.current?.getBoundingClientRect();
        const availableTop = 0;
        const availableBottom = window.innerHeight;
        const estimatedMenuHeight = options.length * OPTION_ROW_HEIGHT_PX + MENU_PADDING_PX;
        const spaceBelow = triggerBounds ? availableBottom - triggerBounds.bottom : estimatedMenuHeight;
        const spaceAbove = triggerBounds ? triggerBounds.top - availableTop : 0;

        setPlacement(spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow ? 'above' : 'below');
        setHighlightedIndex(index);
        setOpen(true);
    };

    const selectOption = (index: number): void => {
        const option = options[index];
        // A stale highlightedIndex (options shrank while open) can reach here via
        // Enter/Space; close without selecting rather than dereferencing undefined.
        if (!option) {
            setOpen(false);
            return;
        }
        if (option.code !== value) {
            onChange(option.code);
        }
        setOpen(false);
        window.requestAnimationFrame(() => triggerRef.current?.focus());
    };

    const moveHighlight = (direction: number): void => {
        if (!open) {
            openList(selectedIndex);
            return;
        }
        setHighlightedIndex((current) => (current + direction + options.length) % options.length);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveHighlight(1);
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveHighlight(-1);
            return;
        }
        if (event.key === 'Home' && open) {
            event.preventDefault();
            setHighlightedIndex(0);
            return;
        }
        if (event.key === 'End' && open) {
            event.preventDefault();
            setHighlightedIndex(options.length - 1);
            return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && open) {
            event.preventDefault();
            selectOption(highlightedIndex);
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openList();
            return;
        }
        if (event.key === 'Escape' && open) {
            event.preventDefault();
            setOpen(false);
            return;
        }
        if (event.key === 'Tab') {
            setOpen(false);
        }
    };

    // Nothing to render without options; guard the option dereferences below.
    if (!selectedOption) {
        return null;
    }

    return (
        <div className={`currency-select currency-select--${placement} ${open ? 'is-open' : ''}`} ref={rootRef}>
            <button
                ref={triggerRef}
                className="currency-select__trigger"
                type="button"
                disabled={disabled}
                role="combobox"
                aria-labelledby={`${labelId} ${valueId}`}
                aria-controls={listboxId}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-activedescendant={
                    open && options[highlightedIndex]
                        ? `${listboxId}-option-${options[highlightedIndex].code}`
                        : undefined
                }
                onClick={() => (open ? setOpen(false) : openList())}
                onKeyDown={handleKeyDown}
            >
                <span className="currency-select__value" id={valueId}>
                    <b>{selectedOption.code}</b>
                    <small>{selectedOption.name}</small>
                </span>
                <ChevronDown size={16} aria-hidden="true" />
            </button>

            {open && !disabled && (
                <div className="currency-select__list" id={listboxId} role="listbox" aria-labelledby={labelId}>
                    {options.map((option, index) => {
                        const isSelected = option.code === value;
                        const isHighlighted = index === highlightedIndex;
                        return (
                            <button
                                id={`${listboxId}-option-${option.code}`}
                                key={option.code}
                                className={`${isSelected ? 'is-selected' : ''} ${
                                    isHighlighted ? 'is-highlighted' : ''
                                }`}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                onMouseDown={(event) => event.preventDefault()}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                onClick={() => selectOption(index)}
                            >
                                <span>
                                    <b>{option.code}</b>
                                    <small>{option.name}</small>
                                </span>
                                <i>{option.symbol}</i>
                                {isSelected && <Check size={15} aria-hidden="true" />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CurrencySelect;
